import React, { useRef } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Menu, MenuItem } from '@material-ui/core'; 
import { AddBox as AddIcon } from '@material-ui/icons';
import { weightDescription, translateAlign } from '../../../utils/text';
import { ToolbarSection, ToolbarItem } from '../../editor';
import { ToolbarRadio } from '../../editor/Toolbar/ToolbarRadio';
import { blue } from '@material-ui/core/colors'; 

const useStyles = makeStyles((theme) => ({
  colorPreviewWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  colorPreviewText: {
    textAlign: 'right',
    fontWeight: 'bold',
  },
  variableInUseHighlight: {
    backgroundColor: blue[50],
    borderLeft: `3px solid ${blue[500]}`,
    paddingLeft: '13px',
  },
}));

const variables = [
  { key: '{{nombrePaciente}}', label: 'Nombre del Paciente' },
  { key: '{{edadPaciente}}', label: 'Edad del Paciente' },
  
  { key: '{{fechaConsulta}}', label: 'Fecha de consulta' },
  { key: '{{motivoConsulta}}', label: 'Motivo de la consulta' },
  { key: '{{presionArterial}}', label: 'Presión Arterial pre-consulta' },
  { key: '{{frecuenciaCardiaca}}', label: 'Frecuencia Cardíaca pre-consulta' },
  { key: '{{frecuenciaRespiratoria}}', label: 'Frecuencia Respiratoria pre-consulta' },
  { key: '{{glucemiaCapilar}}', label: 'Glucemia Capilar pre-consulta' },
  { key: '{{temperatura}}', label: 'Temperatura pre-consulta' },
  { key: '{{saturacionOxigeno}}', label: 'Saturación de Oxígeno pre-consulta' },
  { key: '{{talla}}', label: 'Talla pre-consulta' },
  { key: '{{peso}}', label: 'Peso pre-consulta' },
  { key: '{{alergias}}', label: 'Alergias pre-consulta' },
  
  { key: '{{diagnostico}}', label: 'Diagnóstico' },
  { key: '{{listaMedicamentos}}', label: 'Lista de Medicamentos' },
  
  { key: '{{nombreMedico}}', label: 'Nombre del Médico' },
  { key: '{{especialidadMedico}}', label: 'Especialidad del Médico' },
  { key: '{{cedulaMedico}}', label: 'Cédula Profesional del Médico' },
  { key: '{{direccionClinica}}', label: 'Dirección de la Clínica' },
  { key: '{{nombreClinica}}', label: 'Nombre de la Clínica' },
  { key: '{{telefonoClinica}}', label: 'Teléfono de la Clínica' },
  
  { key: '{{fechaEmision}}', label: 'Fecha de Emisión (Hoy)' },
];

export const TextSettings = ({ nodeId }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { query, actions } = useEditor();

  const lastSelection = useRef(null);
  
  const { dom } = useNode(node => ({
      dom: node.dom
  }));

  React.useEffect(() => {
    if (!dom) return;
    
    const handleSelection = () => {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        lastSelection.current = selection.getRangeAt(0);
      }
    };
    
    dom.addEventListener('keyup', handleSelection);
    dom.addEventListener('mouseup', handleSelection);

    return () => {
      dom.removeEventListener('keyup', handleSelection);
      dom.removeEventListener('mouseup', handleSelection);
    };
  }, [dom]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget); 
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleInsertVariable = (variableKey) => {
    if (!nodeId || !dom) return;
    
    dom.focus();

    if (lastSelection.current) {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(lastSelection.current);
    }
    
    document.execCommand('insertText', false, variableKey);

    handleClose();
  };
  
  const fullTemplateString = query.serialize();

  return (
    <React.Fragment>
      <ToolbarSection title="Variables Dinámicas">
        <Box width="100%">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClick}
              startIcon={<AddIcon />}
              fullWidth
            >Insertar Variable</Button>
        </Box>
      </ToolbarSection>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ style: { maxHeight: 400, width: '30ch' } }} 
      >
        {variables.map((v) => {
          const isUsedInTemplate = fullTemplateString.includes(v.key);

          return (
            <MenuItem 
              key={v.key}
              className={isUsedInTemplate ? classes.variableInUseHighlight : null}
              onClick={() => handleInsertVariable(v.key)}
            >
              {v.label}
            </MenuItem>
          );
        })}
      </Menu>
      <ToolbarSection
        title="Tipografía"
        props={['fontSize', 'fontWeight', 'textAlign']}
        summary={({ fontSize, fontWeight, textAlign }) => {
          return `${fontSize || ''}px, ${weightDescription(
            fontWeight
          )}, ${translateAlign(textAlign)}`;
        }}
      >
        <ToolbarItem
          full={true}
          propKey="fontSize"
          type="slider"
          label="Tamaño de Fuente"
        />
        <ToolbarItem propKey="textAlign" type="radio" label="Alineación">
          <ToolbarRadio value="left" label="Izquierda" />
          <ToolbarRadio value="center" label="Centro" />
          <ToolbarRadio value="right" label="Derecha" />
        </ToolbarItem>
        <ToolbarItem propKey="fontWeight" type="radio" label="Peso">
          <ToolbarRadio value="400" label="Normal" />
          <ToolbarRadio value="500" label="Medio" />
          <ToolbarRadio value="700" label="Negrita" />
        </ToolbarItem>
      </ToolbarSection>
      <ToolbarSection
        title="Márgenes"
        props={['margin']}
        summary={({ margin }) => {
          return `${margin[0] || 0}px ${margin[1] || 0}px ${margin[2] || 0}px ${margin[3] || 0}px`;
        }}
      >
        <ToolbarItem propKey="margin" index={0} type="slider" label="Superior" />
        <ToolbarItem propKey="margin" index={1} type="slider" label="Derecho" />
        <ToolbarItem propKey="margin" index={2} type="slider" label="Inferior" />
        <ToolbarItem propKey="margin" index={3} type="slider" label="Izquierdo" />
      </ToolbarSection>
      <ToolbarSection
        title="Apariencia"
        props={['color', 'shadow']}
        summary={({ color, shadow }) => {
          return (
            <div className={classes.colorPreviewWrapper}>
              <p
                className={classes.colorPreviewText}
                style={{
                  color: color && `rgba(${Object.values(color)})`,
                  textShadow: `0px 0px 2px rgba(0, 0, 0, ${shadow / 100})`,
                }}
              >
                T
              </p>
            </div>
          );
        }}
      >
        <ToolbarItem full={true} propKey="color" type="color" label="Color de Texto" />
        <ToolbarItem full={true} propKey="shadow" type="slider" label="Sombra" />
      </ToolbarSection>
    </React.Fragment>
  );
};